import { useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { supabase } from '../../shared/supabase/supabaseClient';
import {
  getRunningWebBuildId,
  isWebBuildStale,
  parseVersionJsonBuildId,
} from './webBuildVersion';

/**
 * Detecta cuando el deploy web vigente es distinto al bundle que corre la
 * pestaña. Dos canales:
 *  - Realtime sobre app_release_config.web_build_id (instantáneo al deployar).
 *  - GET /version.json al volver al foco (fallback si Realtime no conectó).
 *
 * Devuelve true cuando hay que mostrar el overlay de recarga. Solo web;
 * nativo usa el force-update de appVersionGate.
 */
export function useWebBuildVersionCheck(): boolean {
  const [stale, setStale] = useState(false);
  const runningRef = useRef<string | null>(getRunningWebBuildId());

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const running = runningRef.current;
    if (!running) return; // build sin EXPO_PUBLIC_BUILD_ID → check deshabilitado

    const markIfStale = (deployedId: string | null) => {
      if (isWebBuildStale(running, deployedId)) setStale(true);
    };

    // Fallback: version.json al volver al foco (y una vez al montar).
    const fetchDeployedBuild = async () => {
      try {
        const res = await fetch('/version.json', { cache: 'no-store' });
        if (!res.ok) return;
        markIfStale(parseVersionJsonBuildId(await res.json()));
      } catch {
        // sin red o json malformado → no hacer nada, se reintenta al próximo foco
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void fetchDeployedBuild();
    };
    void fetchDeployedBuild();
    document.addEventListener('visibilitychange', onVisibility);

    // Push: Realtime avisa al instante cuando report-web-build actualiza la fila.
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let cancelled = false;
    void (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token || cancelled) return;
      await supabase.realtime.setAuth(session.access_token);
      if (cancelled) return;
      channel = supabase
        .channel('app-release-config-negocio')
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'app_release_config',
            filter: 'app=eq.negocio',
          },
          (payload) => {
            const next = (payload.new as { web_build_id?: unknown } | null)?.web_build_id;
            markIfStale(typeof next === 'string' ? next : null);
          },
        )
        .subscribe();
    })();

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      if (channel) void supabase.removeChannel(channel);
    };
  }, []);

  return stale;
}
